import axiosInstance from './axiosInstance';
import { ADS as LOCAL_ADS, getActiveAds as getLocalActiveAds, getAdById as getLocalAdById } from '../shared/constants/ads';

/**
 * 광고 (Castar Ads)
 *
 * 백엔드에 /api/ads, /api/admin/ads 가 아직 없을 수 있다.
 * 그동안은 src/shared/constants/ads.js 의 로컬 데이터로 동작하고,
 * 백엔드가 준비되면 isAdApiUnavailable() 분기가 자동으로 실제 API를 타게 된다.
 * (partnerApi.js 와 동일한 패턴)
 */

/** 백엔드 미배포 구간을 화면에서 구분하기 위한 헬퍼 */
export const isAdApiUnavailable = (err) => {
  const s = err?.response?.status;
  return s === 404 || s === 405 || s === 501;
};

/**
 * 목록 요청이 서버까지 닿은 적이 있는지. null이면 아직 모른다.
 *
 * 상세는 '아직 API가 없음'과 '그런 광고가 없음'이 똑같이 404로 온다.
 * 목록이 한 번이라도 서버에서 왔다면 API는 있는 것이므로, 그 뒤의 404는
 * 광고가 없는 것으로 본다 — 숨기거나 기간이 지난 광고가 로컬 데이터로 되살아나면 안 된다.
 */
let apiReachable = null;

const extractList = (body, keys) => {
  if (Array.isArray(body)) return body;
  if (!body || typeof body !== 'object') return null;
  for (const k of keys) if (Array.isArray(body[k])) return body[k];
  if (body.data) return extractList(body.data, keys);
  return null;
};

/** 지금 노출 기간 안에 있는 광고 목록 (공개) */
export const getActiveAds = async () => {
  try {
    const res = await axiosInstance.get('/api/ads');
    const list = extractList(res.data, ['ads', 'content', 'list', 'items']);
    if (!list) throw new Error('광고 목록 응답 형식을 해석할 수 없습니다.');
    apiReachable = true;
    return list;
  } catch (err) {
    if (!isAdApiUnavailable(err)) throw err;
    apiReachable = false;
    return getLocalActiveAds();
  }
};

/** 광고 상세 (공개) */
export const getAdById = async (id) => {
  try {
    const res = await axiosInstance.get(`/api/ads/${id}`);
    apiReachable = true;
    return res.data;
  } catch (err) {
    if (!isAdApiUnavailable(err)) throw err;
    // API가 있는데 404라면 그런 광고가 없는 것이다 (숨김·기간 종료 포함)
    if (apiReachable) return null;
    return getLocalAdById(id);
  }
};

// ── 어드민 ────────────────────────────────────────────────────────────────────

/** 어드민 목록 — 숨김(enabled=false) 광고도 포함 */
export const getAdminAds = async () => {
  try {
    const res = await axiosInstance.get('/api/admin/ads');
    const list = extractList(res.data, ['ads', 'content', 'list', 'items']);
    if (!list) throw new Error('광고 목록 응답 형식을 해석할 수 없습니다.');
    return list;
  } catch (err) {
    if (!isAdApiUnavailable(err)) throw err;
    // 로컬 폴백 상태에서는 ads.js에 등록된 전체 목록(숨김 포함)을 그대로 보여준다.
    return LOCAL_ADS;
  }
};

export const createAd = async (data) => {
  const res = await axiosInstance.post('/api/admin/ads', data);
  return res.data;
};

export const updateAd = async (id, data) => {
  const res = await axiosInstance.put(`/api/admin/ads/${id}`, data);
  return res.data;
};

export const deleteAd = async (id) => {
  await axiosInstance.delete(`/api/admin/ads/${id}`);
};

/** 노출/숨김 전환 (enabled 토글) */
export const updateAdVisibility = async (id, enabled) => {
  const res = await axiosInstance.patch(`/api/admin/ads/${id}/visibility`, { enabled });
  return res.data;
};
